import React from 'react';
import "./GuidedExperienceWizard.css";
import { useTranslation } from 'react-i18next';
import DataTable from '../common/DataTable/DataTable';
import AIRPNumberInput from '../common/AIRPNumberInput/AIRPNumberInput';
import TenantName from './TenantName';

interface LeaseRateWizardProps {
    updateWizardFunction : (e: number | null, i: number, f: string, n?: boolean) => void;
    tenantList: Record<string, unknown>[];
    floorDescriptionLookup: Record<string, unknown>;
    unitSubtypeLookup: Record<string, unknown>;
    // for pagination
    totalPages: number | undefined;
    currentPage: number;
    changePage: (e: number) => void;
}

export default function LeaseRateWizard(props: LeaseRateWizardProps): JSX.Element {
    const { t } = useTranslation("guided_wizard");

    const handleCurrencyChange = (value: number | null, name: string) => {
        const nameArr = name.split("-");
        props.updateWizardFunction(value, parseInt(nameArr[1]), nameArr[0], true);
    };

    const headings = [<th className='small-column' key="lease-rate-wizard-heading-1">{t("unit-heading.floor-level")}</th>,
        <th key="lease-rate-wizard-heading-2">{t("unit-heading.tenant-name")}</th>,
        <th key="lease-rate-wizard-heading-3">{t("unit-heading.unit-number")}</th>,
        <th key="lease-rate-wizard-heading-4">{t("unit-heading.unit-use")}</th>,
        <th className='med-column no-wrap-column' key="lease-rate-wizard-heading-5">{t("unit-heading.current-rent")}</th>,
        <th className='med-column no-wrap-column' key="lease-rate-wizard-heading-6">{t("unit-heading.tax-recovery")}</th>,
        <th className='med-column no-wrap-column' key="lease-rate-wizard-heading-7">{t("unit-heading.operating-recovery")}</th>,
        <th className='med-column no-wrap-column' key="lease-rate-wizard-heading-8">{t("unit-heading.tenant-improvement")}</th>]

        const buildTableContent = (): JSX.Element => {
            const tableBody: JSX.Element[] = [];

            for (let index = 10 * (props.currentPage - 1); index < Math.min(props.tenantList.length, 10 * props.currentPage); index++) {
                tableBody.push (<tr key={`lease-rate-wizard-row-${index}`}>
                    <td className='no-input-cell'><label>{props.floorDescriptionLookup[props.tenantList[index]["floorLevel"] as string] as string}</label></td>
                    <td className='no-input-cell tenant-name-cell'><TenantName name={props.tenantList[index]["tenantName"] as string} /></td>
                    <td className='no-input-cell tenant-unit-cell'><p>{props.tenantList[index]["tenantUnitNumber"] as string}</p></td>
                    <td className='no-input-cell'><p>{props.unitSubtypeLookup[props.tenantList[index]["unitSubtypeCode"] as string] as string}</p></td>
                    <td className={'no-padding-cell wizard-grey-out-cell med-column'}>
                        {(props.tenantList[index]["leaseStatusCode"] as string) !== "1" && 
                        <AIRPNumberInput
                            negatives={false}
                            symbol='$'
                            initialValue={props.tenantList[index]["currentBaseRent"] as number | undefined}
                            name={`currentBaseRent-${index}`}
                            onChange={(value: number | null) => handleCurrencyChange(value, `currentBaseRent-${index}`)}
                        />
                    }</td>
                    <td className={'no-padding-cell wizard-grey-out-cell med-column'}>
                        {((props.tenantList[index]["leaseStatusCode"] as string) !== "1" && (props.tenantList[index]["leaseTypeCode"] as string) !== "2") && 
                        <AIRPNumberInput
                            negatives={false}
                            symbol='$'
                            initialValue={props.tenantList[index]["propertyTaxRecovery"] as number | undefined}
                            name={`propertyTaxRecovery-${index}`}
                            onChange={(value: number | null) => handleCurrencyChange(value, `propertyTaxRecovery-${index}`)}
                        />
                    }</td>
                    <td className={'no-padding-cell wizard-grey-out-cell med-column'}>
                        {((props.tenantList[index]["leaseStatusCode"] as string) !== "1" && (props.tenantList[index]["leaseTypeCode"] as string) !== "2") && 
                        <AIRPNumberInput
                            negatives={false}
                            symbol='$'
                            initialValue={props.tenantList[index]["operatingRecovery"] as number | undefined}
                            name={`operatingRecovery-${index}`}
                            onChange={(value: number | null) => handleCurrencyChange(value, `operatingRecovery-${index}`)}
                        />
                    }</td>
                    <td className={'no-padding-cell wizard-grey-out-cell med-column'}>
                        {(props.tenantList[index]["leaseStatusCode"] as string) !== "1" && 
                        <AIRPNumberInput
                            negatives={false}
                            symbol='$'
                            initialValue={props.tenantList[index]["tenantImprovement"] as number | undefined}
                            name={`tenantImprovement-${index}`}
                            onChange={(value: number | null) => handleCurrencyChange(value, `tenantImprovement-${index}`)}
                        />}
                    </td>
                </tr>)
            }
            return (<>{tableBody}</>);
        }

    return (<div>
        <p>{t("lease-rate-wizard.lease-rate-description")}</p>
        <DataTable headings={headings} className='wizard-table multires-full-table' totalPages={props.totalPages} currentPage={props.currentPage} changePage={props.changePage}>
            {buildTableContent()}
        </DataTable>
    </div>)
}
